import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TelefonoConPaisInput, { getPaisTelefonoDefaultId } from '../../components/common/TelefonoConPaisInput';
import ProspectoDuplicadosAlert from '../../components/crm/ProspectoDuplicadosAlert';
import {
  crearProspectoCrm,
  ESTATUS_CREDITO,
  ESTATUS_PROSPECTO_CRM,
  ESTATUS_PROSPECTO_LABELS,
  FINANCIAMIENTOS,
  FINANCIAMIENTO_LABELS,
  getCatalogoResponsables,
  labelFromMap,
  normalizeProspectoPayload,
  OPERACIONES_BUSQUEDA,
  ORIGENES_PROSPECTO,
  ORIGEN_LABELS,
  TEMPERATURAS_PROSPECTO,
  TEMPERATURA_LABELS,
  TIPOS_INMUEBLE_BUSQUEDA,
  validarDuplicadosProspecto,
} from '../../services/prospectosCrmService';
import { getCodigosNumeroPaises } from '../../services/catalogosService';
import { obtenerAdminInmuebles } from '../../services/adminInmueblesService';
import { listarAdminDesarrollos } from '../../services/adminDesarrollosService';
import { listarProyectos } from '../../services/proyectosInmobiliariosService';
import { listarModelos } from '../../services/proyectoModelosService';
import { listarUnidades } from '../../services/proyectoUnidadesService';
import useAuthSession from '../../hooks/useAuthSession';
import '../AdminProspectosPage.css';

const TIPO_CARDS = [
  { value: 'INTERES_ESPECIFICO', title: 'Interesado en propiedad o proyecto', text: 'Tiene interes por una opcion concreta.' },
  { value: 'BUSQUEDA_ACTIVA', title: 'Cliente que busca propiedad', text: 'Necesita que CN encuentre opciones.' },
  { value: 'INVERSIONISTA', title: 'Inversionista', text: 'Evalua opciones por rendimiento o plusvalia.' },
  { value: 'CAPTACION_PROPIETARIO', title: 'Propietario que desea vender o rentar', text: 'Contacto para oportunidad comercial de captacion.' },
  { value: 'OTRO', title: 'Otro', text: 'Caso comercial fuera de los flujos principales.' },
];

const INITIAL_FORM = {
  tipoProspecto: 'INTERES_ESPECIFICO',
  nombre: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  codigoNumeroPaisId: '',
  telefono: '',
  email: '',
  medioContactoPreferido: 'WHATSAPP',
  origen: 'CAPTURA_MANUAL',
  origenDetalle: '',
  inmuebleId: '',
  desarrolloId: '',
  proyectoId: '',
  modeloId: '',
  unidadId: '',
  mensajeInicial: '',
  tipoOperacionBuscada: '',
  tipoInmuebleBuscado: '',
  zonaInteres: '',
  zonasAlternativas: '',
  presupuestoMinimo: '',
  presupuestoMaximo: '',
  engancheDisponible: '',
  tipoFinanciamiento: '',
  estatusCredito: '',
  montoCredito: '',
  institucionCredito: '',
  recamarasMinimas: '',
  banosMinimos: '',
  estacionamientosMinimos: '',
  superficieTerrenoMinima: '',
  superficieConstruccionMinima: '',
  aceptaUsado: false,
  aceptaPreventa: false,
  aceptaMunicipiosCercanos: false,
  fechaEstimadaCompra: '',
  requisitosBusqueda: '',
  captacionTipoOperacion: '',
  captacionTipoInmueble: '',
  captacionUbicacion: '',
  captacionDescripcion: '',
  captacionPrecioEstimado: '',
  estatus: 'NUEVO',
  temperatura: 'TIBIO',
  fechaProximoSeguimiento: '',
  asesorId: '',
  supervisorId: '',
};

const getApiErrorMessage = (err) =>
  err.data?.mensaje || err.data?.message || err.message || 'No fue posible procesar la accion.';

const optionLabel = (value) => labelFromMap({}, value, '');

export default function AdminProspectoFormPage() {
  const navigate = useNavigate();
  const sesion = useAuthSession();
  const [form, setForm] = useState(INITIAL_FORM);
  const [paises, setPaises] = useState([]);
  const [responsables, setResponsables] = useState({ asesores: [], supervisores: [] });
  const [inmuebles, setInmuebles] = useState([]);
  const [desarrollos, setDesarrollos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [duplicados, setDuplicados] = useState([]);
  const [duplicadosRevisados, setDuplicadosRevisados] = useState(false);
  const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [cargandoResponsables, setCargandoResponsables] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [validandoDuplicados, setValidandoDuplicados] = useState(false);
  const [errorResponsables, setErrorResponsables] = useState('');
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const rolGlobal = String(sesion.rolGlobal || '').toUpperCase();
  const puedeAsignar = ['ADMIN', 'SUPERADMIN', 'SUPERVISOR'].includes(rolGlobal) || sesion.esAdminCn;
  const mostrarInteres = form.tipoProspecto === 'INTERES_ESPECIFICO';
  const mostrarBusqueda = ['BUSQUEDA_ACTIVA', 'INVERSIONISTA'].includes(form.tipoProspecto);
  const mostrarCaptacion = form.tipoProspecto === 'CAPTACION_PROPIETARIO';
  const mostrarCredito = form.tipoFinanciamiento && form.tipoFinanciamiento !== 'CONTADO';

  const cargarResponsables = useCallback(async (options = {}) => {
    setCargandoResponsables(true);
    setErrorResponsables('');

    try {
      setResponsables(await getCatalogoResponsables(options));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('No fue posible cargar responsables CRM', err);
        setErrorResponsables('No fue posible cargar responsables comerciales.');
      }
    } finally {
      if (!options.signal?.aborted) {
        setCargandoResponsables(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const cargarCatalogos = async () => {
      setCargando(true);
      setError('');

      try {
        const [paisesData, inmueblesData, desarrollosData, proyectosData] = await Promise.all([
          getCodigosNumeroPaises({ signal: controller.signal }).catch(() => []),
          obtenerAdminInmuebles({ signal: controller.signal }).catch(() => []),
          listarAdminDesarrollos({ signal: controller.signal }).catch(() => []),
          listarProyectos({ soloActivos: true, signal: controller.signal }).catch(() => []),
        ]);
        setPaises(paisesData);
        setInmuebles(inmueblesData);
        setDesarrollos(desarrollosData);
        setProyectos(proyectosData);
        setForm((actual) => ({
          ...actual,
          codigoNumeroPaisId: actual.codigoNumeroPaisId || getPaisTelefonoDefaultId(paisesData),
        }));
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(getApiErrorMessage(err));
        }
      } finally {
        if (!controller.signal.aborted) {
          setCargando(false);
        }
      }
    };

    cargarCatalogos();
    cargarResponsables({ signal: controller.signal });
    return () => controller.abort();
  }, [cargarResponsables]);

  const cargarDatosProyecto = useCallback(async (proyectoId) => {
    if (!proyectoId) {
      setModelos([]);
      setUnidades([]);
      return;
    }

    const [modelosData, unidadesData] = await Promise.all([
      listarModelos(proyectoId, { soloActivos: true }).catch(() => []),
      listarUnidades(proyectoId, { soloActivas: true }).catch(() => []),
    ]);
    setModelos(modelosData);
    setUnidades(unidadesData);
  }, []);

  useEffect(() => {
    cargarDatosProyecto(form.proyectoId);
  }, [cargarDatosProyecto, form.proyectoId]);

  const duplicadoPayload = useMemo(() => ({
    telefono: form.telefono.trim() || null,
    codigoNumeroPaisId: form.codigoNumeroPaisId || null,
    email: form.email.trim() || null,
    excluirProspectoId: null,
  }), [form.codigoNumeroPaisId, form.email, form.telefono]);

  const update = (event) => {
    const { checked, name, type, value } = event.target;
    setForm((actual) => {
      const next = { ...actual, [name]: type === 'checkbox' ? checked : value };
      if (name === 'tipoProspecto') {
        next.modeloId = '';
        next.unidadId = '';
      }
      if (name === 'proyectoId') {
        next.modeloId = '';
        next.unidadId = '';
      }
      return next;
    });
    if (['telefono', 'email', 'codigoNumeroPaisId'].includes(name)) {
      setDuplicadosRevisados(false);
      setDuplicados([]);
    }
  };

  const validarDuplicados = async () => {
    if (!duplicadoPayload.telefono && !duplicadoPayload.email) {
      setError('Captura telefono o correo para validar duplicados.');
      return [];
    }

    setValidandoDuplicados(true);
    setError('');

    try {
      const data = await validarDuplicadosProspecto(duplicadoPayload);
      setDuplicados(data);
      setDuplicadosRevisados(true);
      setConfirmarDuplicado(data.length > 0);
      return data;
    } catch (err) {
      setError(getApiErrorMessage(err));
      return [];
    } finally {
      setValidandoDuplicados(false);
    }
  };

  const validarFormulario = () => {
    if (!form.nombre.trim()) return 'El nombre es obligatorio.';
    if (!form.telefono.trim() && !form.email.trim()) return 'Captura telefono o correo.';
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'El correo no tiene un formato valido.';
    return '';
  };

  const guardar = async (event, force = false) => {
    event?.preventDefault?.();
    setError('');
    setMensaje('');

    const validacion = validarFormulario();
    if (validacion) {
      setError(validacion);
      return;
    }

    if (!force && !duplicadosRevisados && (duplicadoPayload.telefono || duplicadoPayload.email)) {
      const encontrados = await validarDuplicados();
      if (encontrados.length > 0) {
        return;
      }
    }

    setGuardando(true);

    try {
      const prospecto = await crearProspectoCrm(normalizeProspectoPayload(form));
      setMensaje('Prospecto creado correctamente.');
      navigate(`/admin/prospectos/${prospecto.id || prospecto.prospectoId}`);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
      setConfirmarDuplicado(false);
    }
  };

  return (
    <main className="crm-prospectos">
      <section className="crm-prospectos-hero">
        <div>
          <p className="crm-prospectos-eyebrow">CRM central</p>
          <h1>Nuevo prospecto</h1>
          <span>Captura contacto, origen, interes y proximo seguimiento.</span>
        </div>
        <Link to="/admin/prospectos">Volver a prospectos</Link>
      </section>

      {cargando ? <p className="crm-prospectos-feedback">Cargando catalogos...</p> : null}
      {mensaje ? <p className="crm-prospectos-feedback is-ok">{mensaje}</p> : null}
      {error ? <p className="crm-prospectos-feedback is-error">{error}</p> : null}

      <form className="crm-prospectos-form" onSubmit={guardar}>
        <section className="crm-prospectos-card">
          <div className="crm-prospectos-section-head">
            <h2>Tipo de prospecto</h2>
          </div>
          <div className="crm-prospectos-type-grid">
            {TIPO_CARDS.map((tipo) => (
              <label key={tipo.value} className={form.tipoProspecto === tipo.value ? 'is-selected' : ''}>
                <input type="radio" name="tipoProspecto" value={tipo.value} checked={form.tipoProspecto === tipo.value} onChange={update} />
                <strong>{tipo.title}</strong>
                <span>{tipo.text}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="crm-prospectos-card">
          <div className="crm-prospectos-section-head">
            <h2>Datos de contacto</h2>
            <button type="button" onClick={validarDuplicados} disabled={validandoDuplicados || (!form.telefono && !form.email)}>
              {validandoDuplicados ? 'Validando...' : 'Validar duplicados'}
            </button>
          </div>
          <div className="crm-prospectos-form-grid">
            <label><span>Nombre</span><input name="nombre" value={form.nombre} onChange={update} required /></label>
            <label><span>Apellido paterno</span><input name="apellidoPaterno" value={form.apellidoPaterno} onChange={update} /></label>
            <label><span>Apellido materno</span><input name="apellidoMaterno" value={form.apellidoMaterno} onChange={update} /></label>
            <label>
              <span>Telefono</span>
              <TelefonoConPaisInput
                paises={paises}
                codigoNumeroPaisId={form.codigoNumeroPaisId}
                telefono={form.telefono}
                onChangePais={update}
                onChangeTelefono={update}
              />
            </label>
            <label><span>Correo</span><input name="email" type="email" value={form.email} onChange={update} /></label>
            <label>
              <span>Medio preferido</span>
              <select name="medioContactoPreferido" value={form.medioContactoPreferido} onChange={update}>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="LLAMADA">Llamada</option>
                <option value="CORREO">Correo</option>
                <option value="INDISTINTO">Indistinto</option>
              </select>
            </label>
            <label>
              <span>Origen</span>
              <select name="origen" value={form.origen} onChange={update}>
                {ORIGENES_PROSPECTO.map((origen) => <option key={origen} value={origen}>{labelFromMap(ORIGEN_LABELS, origen)}</option>)}
              </select>
            </label>
            <label className="is-wide"><span>Detalle del origen</span><input name="origenDetalle" value={form.origenDetalle} onChange={update} /></label>
          </div>
          <ProspectoDuplicadosAlert duplicados={duplicados} />
        </section>

        {mostrarInteres ? (
          <section className="crm-prospectos-card">
            <div className="crm-prospectos-section-head"><h2>Interes especifico</h2></div>
            <div className="crm-prospectos-form-grid">
              <label><span>Inmueble</span><select name="inmuebleId" value={form.inmuebleId} onChange={update}><option value="">Sin inmueble</option>{inmuebles.map((item) => <option key={item.id} value={item.id}>{item.titulo}</option>)}</select></label>
              <label><span>Desarrollo</span><select name="desarrolloId" value={form.desarrolloId} onChange={update}><option value="">Sin desarrollo</option>{desarrollos.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}</select></label>
              <label><span>Proyecto inmobiliario</span><select name="proyectoId" value={form.proyectoId} onChange={update}><option value="">Sin proyecto</option>{proyectos.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}</select></label>
              <label><span>Modelo</span><select name="modeloId" value={form.modeloId} onChange={update} disabled={!form.proyectoId}><option value="">Sin modelo</option>{modelos.map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}</select></label>
              <label><span>Unidad</span><select name="unidadId" value={form.unidadId} onChange={update} disabled={!form.proyectoId}><option value="">Sin unidad</option>{unidades.map((item) => <option key={item.id} value={item.id}>{item.codigo}</option>)}</select></label>
              <label className="is-full"><span>Mensaje inicial / interes especifico</span><textarea name="mensajeInicial" value={form.mensajeInicial} onChange={update} rows="4" /></label>
            </div>
          </section>
        ) : null}

        {mostrarBusqueda ? (
          <section className="crm-prospectos-card">
            <div className="crm-prospectos-section-head"><h2>Busqueda activa</h2></div>
            <div className="crm-prospectos-form-grid">
              <label><span>Operacion buscada</span><select name="tipoOperacionBuscada" value={form.tipoOperacionBuscada} onChange={update}><option value="">Selecciona</option>{OPERACIONES_BUSQUEDA.map((item) => <option key={item} value={item}>{optionLabel(item)}</option>)}</select></label>
              <label><span>Tipo de inmueble</span><select name="tipoInmuebleBuscado" value={form.tipoInmuebleBuscado} onChange={update}><option value="">Selecciona</option>{TIPOS_INMUEBLE_BUSQUEDA.map((item) => <option key={item} value={item}>{optionLabel(item)}</option>)}</select></label>
              <label><span>Zona de interes</span><input name="zonaInteres" value={form.zonaInteres} onChange={update} /></label>
              <label><span>Zonas alternativas</span><input name="zonasAlternativas" value={form.zonasAlternativas} onChange={update} /></label>
              <label><span>Presupuesto minimo</span><input name="presupuestoMinimo" type="number" min="0" value={form.presupuestoMinimo} onChange={update} /></label>
              <label><span>Presupuesto maximo</span><input name="presupuestoMaximo" type="number" min="0" value={form.presupuestoMaximo} onChange={update} /></label>
              <label><span>Enganche disponible</span><input name="engancheDisponible" type="number" min="0" value={form.engancheDisponible} onChange={update} /></label>
              <label><span>Tipo de financiamiento</span><select name="tipoFinanciamiento" value={form.tipoFinanciamiento} onChange={update}><option value="">Selecciona</option>{FINANCIAMIENTOS.map((item) => <option key={item} value={item}>{labelFromMap(FINANCIAMIENTO_LABELS, item)}</option>)}</select></label>
              {mostrarCredito ? (
                <>
                  <label><span>Ya tiene credito</span><select name="estatusCredito" value={form.estatusCredito} onChange={update}><option value="">Selecciona</option>{ESTATUS_CREDITO.map((item) => <option key={item} value={item}>{optionLabel(item)}</option>)}</select></label>
                  <label><span>Monto de credito</span><input name="montoCredito" type="number" min="0" value={form.montoCredito} onChange={update} /></label>
                  <label><span>Institucion de credito</span><input name="institucionCredito" value={form.institucionCredito} onChange={update} /></label>
                </>
              ) : null}
              <label><span>Recamaras minimas</span><input name="recamarasMinimas" type="number" min="0" value={form.recamarasMinimas} onChange={update} /></label>
              <label><span>Banos minimos</span><input name="banosMinimos" type="number" min="0" value={form.banosMinimos} onChange={update} /></label>
              <label><span>Estacionamientos minimos</span><input name="estacionamientosMinimos" type="number" min="0" value={form.estacionamientosMinimos} onChange={update} /></label>
              <label><span>Terreno minimo</span><input name="superficieTerrenoMinima" type="number" min="0" value={form.superficieTerrenoMinima} onChange={update} /></label>
              <label><span>Construccion minima</span><input name="superficieConstruccionMinima" type="number" min="0" value={form.superficieConstruccionMinima} onChange={update} /></label>
              <label><span>Fecha estimada de compra</span><input name="fechaEstimadaCompra" type="date" value={form.fechaEstimadaCompra} onChange={update} /></label>
              <label className="crm-prospectos-check"><input name="aceptaUsado" type="checkbox" checked={form.aceptaUsado} onChange={update} /><span>Acepta usado</span></label>
              <label className="crm-prospectos-check"><input name="aceptaPreventa" type="checkbox" checked={form.aceptaPreventa} onChange={update} /><span>Acepta preventa</span></label>
              <label className="crm-prospectos-check"><input name="aceptaMunicipiosCercanos" type="checkbox" checked={form.aceptaMunicipiosCercanos} onChange={update} /><span>Acepta municipios cercanos</span></label>
              <label className="is-full"><span>Requisitos de busqueda</span><textarea name="requisitosBusqueda" value={form.requisitosBusqueda} onChange={update} rows="4" /></label>
            </div>
          </section>
        ) : null}

        {mostrarCaptacion ? (
          <section className="crm-prospectos-card">
            <div className="crm-prospectos-section-head"><h2>Captacion de propietario</h2></div>
            <div className="crm-prospectos-form-grid">
              <label><span>Operacion que desea realizar</span><select name="captacionTipoOperacion" value={form.captacionTipoOperacion} onChange={update}><option value="">Selecciona</option>{OPERACIONES_BUSQUEDA.map((item) => <option key={item} value={item}>{optionLabel(item)}</option>)}</select></label>
              <label><span>Tipo de inmueble</span><select name="captacionTipoInmueble" value={form.captacionTipoInmueble} onChange={update}><option value="">Selecciona</option>{TIPOS_INMUEBLE_BUSQUEDA.map((item) => <option key={item} value={item}>{optionLabel(item)}</option>)}</select></label>
              <label><span>Zona o ubicacion</span><input name="captacionUbicacion" value={form.captacionUbicacion} onChange={update} /></label>
              <label><span>Precio estimado o expectativa</span><input name="captacionPrecioEstimado" type="number" min="0" value={form.captacionPrecioEstimado} onChange={update} /></label>
              <label className="is-full"><span>Descripcion de la propiedad</span><textarea name="captacionDescripcion" value={form.captacionDescripcion} onChange={update} rows="4" /></label>
            </div>
          </section>
        ) : null}

        <section className="crm-prospectos-card">
          <div className="crm-prospectos-section-head"><h2>Seguimiento y asignacion</h2></div>
          <div className="crm-prospectos-form-grid">
            <label><span>Estatus inicial</span><select name="estatus" value={form.estatus} onChange={update}>{ESTATUS_PROSPECTO_CRM.map((item) => <option key={item} value={item}>{labelFromMap(ESTATUS_PROSPECTO_LABELS, item)}</option>)}</select></label>
            <label><span>Temperatura</span><select name="temperatura" value={form.temperatura} onChange={update}>{TEMPERATURAS_PROSPECTO.map((item) => <option key={item} value={item}>{labelFromMap(TEMPERATURA_LABELS, item)}</option>)}</select></label>
            <label><span>Fecha de proximo seguimiento</span><input name="fechaProximoSeguimiento" type="datetime-local" value={form.fechaProximoSeguimiento} onChange={update} /></label>
            <label>
              <span>Asesor responsable</span>
              <select name="asesorId" value={form.asesorId} onChange={update} disabled={!puedeAsignar || cargandoResponsables}>
                <option value="">{puedeAsignar ? 'Sin asignar' : 'Asignacion automatica'}</option>
                {responsables.asesores.map((asesor) => <option key={asesor.id} value={asesor.id}>{asesor.nombre}</option>)}
              </select>
              {cargandoResponsables ? <small>Cargando responsables...</small> : null}
            </label>
            {puedeAsignar ? (
              <label>
                <span>Supervisor responsable</span>
                <select name="supervisorId" value={form.supervisorId} onChange={update} disabled={cargandoResponsables}>
                  <option value="">Sin supervisor</option>
                  {responsables.supervisores.map((supervisor) => <option key={supervisor.id} value={supervisor.id}>{supervisor.nombre}</option>)}
                </select>
                {cargandoResponsables ? <small>Cargando responsables...</small> : null}
              </label>
            ) : null}
            {errorResponsables ? (
              <p className="crm-prospectos-feedback is-error">
                <span>{errorResponsables}</span>
                <button type="button" onClick={() => cargarResponsables()}>Reintentar</button>
              </p>
            ) : null}
            {puedeAsignar && form.asesorId ? (
              <p className="crm-prospectos-feedback is-info">
                El asesor seleccionado sera responsable del seguimiento. La atribucion comercial se conservara conforme al origen del prospecto.
              </p>
            ) : null}
            <label className="is-full"><span>Notas iniciales</span><textarea name="mensajeInicial" value={form.mensajeInicial} onChange={update} rows="4" /></label>
          </div>
        </section>

        <div className="crm-prospectos-form-actions">
          <button type="submit" className="is-primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar prospecto'}</button>
          <Link to="/admin/prospectos">Cancelar</Link>
        </div>
      </form>

      {confirmarDuplicado ? (
        <div className="crm-prospectos-modal-overlay" role="presentation" onMouseDown={() => setConfirmarDuplicado(false)}>
          <section className="crm-prospectos-modal is-wide" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className="crm-prospectos-modal-head">
              <div>
                <p className="crm-prospectos-eyebrow">Duplicados</p>
                <h2>Posibles coincidencias</h2>
              </div>
              <button type="button" onClick={() => setConfirmarDuplicado(false)} disabled={guardando} aria-label="Cerrar">x</button>
            </div>
            <div className="crm-prospectos-modal-body">
              <ProspectoDuplicadosAlert
                duplicados={duplicados}
                continuing={guardando}
                onCancel={() => setConfirmarDuplicado(false)}
                onContinue={() => guardar(null, true)}
              />
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
